import {useEffect, useState} from 'react';

import type {MountTransitionPhase} from './types';

export interface MountTransitionState {
  /** Controls whether the element is present in the DOM or not */
  shouldMount: boolean;
  /** Apply the exit CSS class when this is 'exit' */
  animationState: MountTransitionPhase;
}

/**
 * Keeps an element in the DOM long enough for its exit animation to finish.
 *
 * `animationState`, and the mounted-while-visible half of `shouldMount`, are
 * derived straight from `isVisible` — there is nothing to synchronise there. The
 * only genuine state is "we are still inside the exit window", and the only
 * thing that can end that window is the clock: it is set from the teardown of
 * the visible phase and cleared by the timer, never synchronously mid-effect.
 */
const useMountTransition = (
  isVisible: boolean,
  transitionDuration: number,
): MountTransitionState => {
  const [isExiting, setIsExiting] = useState(false);

  // effect:audited — defers unmount to the animation clock, an external system
  useEffect(() => {
    /*
     * Entering needs no state at all. Teardown of this branch *is* the hide
     * edge, so marking the exit there keeps the element mounted from the very
     * next render without a synchronous set in the effect body.
     */
    if (isVisible) return () => setIsExiting(true);

    const timeout = setTimeout(() => setIsExiting(false), transitionDuration);

    return () => clearTimeout(timeout);
  }, [isVisible, transitionDuration]);

  return {
    shouldMount: isVisible || isExiting,
    animationState: isVisible ? 'enter' : 'exit',
  };
};

export default useMountTransition;
